import {Event} from "../types/event"

export type AnalysisHistoryEntry = {
    /** Identifies the analysed fixture, so re-analysing the same match refreshes its entry */
    id: string
    homeName: string
    awayName: string
    leagueName?: string
    /** Kick off of the analysed match, as returned by the prediction api */
    startAt?: string
    analyzedAt: number
    /** The analysis itself, kept whole so an entry can be reopened without asking the api again */
    event: Event
}

const STORAGE_PREFIX = "scorevision_analysis_history"

/**
 * Analyses are worth keeping but not worth filling the storage quota with: an event carries its
 * predictions and odds, so a handful of kilobytes each.
 */
const HISTORY_LIMIT = 30

/**
 * History is per account: a shared browser must not show one user the matches another one analysed.
 * Nothing is kept for a visitor, the analysis screens ask for an account first.
 */
const storageKey = (userId?: string) => userId ? `${STORAGE_PREFIX}:${userId}` : null

const eventKey = (event: Event) =>
    `${event.id ?? ""}:${event.team_home?.id ?? event.team_home?.name}:${event.team_away?.id ?? event.team_away?.name}`

/** Storage is unavailable in private browsing modes, where the history simply stays empty */
const read = (key: string): AnalysisHistoryEntry[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || "[]")
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const write = (key: string, entries: AnalysisHistoryEntry[]) => {
    try {
        localStorage.setItem(key, JSON.stringify(entries))
    } catch {
        // A full or unavailable storage costs the history, not the analysis the user just ran
    }
}

export const readAnalysisHistory = (userId?: string): AnalysisHistoryEntry[] => {
    const key = storageKey(userId)
    if (!key) return []

    return read(key).sort((a, b) => b.analyzedAt - a.analyzedAt)
}

/** Returns the history as it stands afterwards, so a caller can render it without reading again */
export const recordAnalysis = (userId: string | undefined, event: Event): AnalysisHistoryEntry[] => {
    const key = storageKey(userId)
    if (!key || !event) return []

    const entry: AnalysisHistoryEntry = {
        id: eventKey(event),
        homeName: event.team_home?.name,
        awayName: event.team_away?.name,
        leagueName: event.league?.name,
        startAt: event.start_at,
        analyzedAt: Date.now(),
        event
    }

    const entries = [entry, ...read(key).filter(previous => previous.id !== entry.id)]
        .slice(0, HISTORY_LIMIT)

    write(key, entries)
    return entries
}

export const removeAnalysis = (userId: string | undefined, id: string): AnalysisHistoryEntry[] => {
    const key = storageKey(userId)
    if (!key) return []

    const entries = read(key).filter(entry => entry.id !== id)
    write(key, entries)
    return entries
}

export const clearAnalysisHistory = (userId?: string) => {
    const key = storageKey(userId)
    if (key) write(key, [])
}
